import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Table } from '../../../shared/table/table';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { faArrowLeft, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Setup } from '../../shared/service/setup';
import { ToastService } from '../../../../core/shared/services/toast.service';
import { CommonCodeValue } from '../../shared/models/common-code-value.model';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';

@Component({
  selector: 'app-reference-group-values',
  imports: [TranslatePipe, Table, Popup, FontAwesomeModule],
  templateUrl: './reference-group-values.html',
  styleUrl: './reference-group-values.css',
})
export class ReferenceGroupValues implements OnInit {
  showPopup = false;
  faPencil = faPencil;
  faTrash = faTrash;
  faArrowLeft = faArrowLeft;
  popupTitle = '';
  isDeleteConfirm = false;
  deleteMessage = '';

  grpSeq: number = 0;
  values: CommonCodeValue[] = [];
  selectedRow: CommonCodeValue | null = null;
  loading = false;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    public router: Router,
    private setupService: Setup,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.grpSeq = Number(params['grpSeq']);
      if (this.grpSeq) {
        this.loadValues();
      }
    });
  }

  loadValues() {
    this.loading = true;
    this.setupService.getCommonCodeValues(this.grpSeq).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.values = Array.isArray(res) ? res : res.data || [];
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load values', err);
      }
    });
  }

  popupFields: DynamicFieldConfig[] = [
    { label: 'COMMON.NAME', key: 'refCdDscr', type: 'text' },
    { label: 'SETUP.SORT_ORDER', key: 'sortOrder', type: 'number' },
    { label: 'COMMON.STATUS', key: 'refCdActvFlg', type: 'select', options$: [{ label: 'COMMON.ACTIVE', value: true }, { label: 'COMMON.INACTIVE', value: false }] }
  ];

  // ================= TABLE CONFIG =================
  columns: TableColumn[] = [
    { key: 'refCdSeq', label: 'COMMON.CODE', type: 'text' },
    { key: 'refCdDscr', label: 'COMMON.NAME', type: 'text' },
    { key: 'sortOrder', label: 'SETUP.SORT_ORDER', type: 'text' },
    {
      key: 'refCdActvFlg',
      label: 'COMMON.STATUS',
      type: 'badge',
      map: { true: 'COMMON.ACTIVE', false: 'COMMON.INACTIVE' },
      badgeMap: {
        true: 'status-active',
        false: 'status-inactive'
      }
    },
  ];

  openRefCdVal() {
    this.selectedRow = null;
    this.popupTitle = 'SETUP.COMMON_CODES.ADD_VALUE';
    this.isDeleteConfirm = false;
    this.popupFields = [
      { label: 'COMMON.NAME', key: 'refCdDscr', type: 'text', value: '', validations: { required: true } },
      { label: 'SETUP.SORT_ORDER', key: 'sortOrder', type: 'number', value: '' },
      { label: 'COMMON.STATUS', key: 'refCdActvFlg', type: 'select', value: true, options$: [{ label: 'COMMON.ACTIVE', value: true }, { label: 'COMMON.INACTIVE', value: false }] }
    ];
    this.showPopup = true;
  }

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.COMMON_CODES.EDIT_VALUE';
        this.isDeleteConfirm = false;
        this.popupFields = [
          { label: 'COMMON.NAME', key: 'refCdDscr', type: 'text', value: row.refCdDscr, validations: { required: true } },
          { label: 'SETUP.SORT_ORDER', key: 'sortOrder', type: 'number', value: row.sortOrder },
          {
            label: 'COMMON.STATUS',
            key: 'refCdActvFlg',
            type: 'select',
            value: row.refCdActvFlg, // true/false
            options$: [{ label: 'COMMON.ACTIVE', value: true }, { label: 'COMMON.INACTIVE', value: false }]
          }
        ];
        this.showPopup = true;
      }
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.COMMON_CODES.DELETE_VALUE';
        this.isDeleteConfirm = true;
        this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.refCdDscr });
        this.showPopup = true;
      }
    }
  ];

  SaveCommonCodeValues(formData: any): void {
    const payload = this.createPayload(formData);
    console.log('Validating Payload:', payload);

    this.setupService.saveCommonCodeValue(payload).subscribe({
      next: (res) => {
        this.toast.success(this.isDeleteConfirm ? 'SETUP.DELETED_SUCCESSFULLY' : 'SETUP.SAVED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadValues();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('SETUP.FAILED_TO_SAVE_RECORD');
      }
    });
  }

  onDeleteConfirmed(): void {
    this.SaveCommonCodeValues({});
  }

  private createPayload(formData: any): CommonCodeValue {
    const isDelete = this.isDeleteConfirm;

    return {
      tntKey: 0,
      refCdSeq: this.selectedRow ? this.selectedRow.refCdSeq : null,
      grpSeq: this.grpSeq,

      // If Delete, use existing values. If Edit/Create, use Form values.
      refCd: this.selectedRow?.refCd || "",
      refCdDscr: (isDelete && this.selectedRow) ? this.selectedRow.refCdDscr : formData.refCdDscr,
      sortOrder: (isDelete && this.selectedRow) ? this.selectedRow.sortOrder : (Number(formData.sortOrder) || 0),
      refCdActvFlg: (isDelete && this.selectedRow) ? this.selectedRow.refCdActvFlg : (formData.refCdActvFlg ?? true),

      delFlg: isDelete ? 1 : 0
    };
  }

}
